import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import {
  createDemoRequest,
  joinWaitlist,
  DemoRequestPayload,
  WaitlistPayload,
} from "../../services/landing.service";
import { ROLES } from "../../constants/general.constant";
import { Button, CircularProgress } from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SlideshowIcon from "@mui/icons-material/Slideshow";

type DemoFormValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  message: string;
};

type WaitlistFormValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  companyName: string;
  role: string;
  dealsPerMonth: string;
};

type FieldErrors<T> = Partial<Record<keyof T, string>>;

const getApiErrorMessage = (e: any) => {
  return (
    e?.response?.data?.message ||
    e?.message ||
    "Something went wrong. Please try again."
  );
};

const FormsSection = () => {
  const [demoSuccess, setDemoSuccess] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [demoSubmitError, setDemoSubmitError] = useState<string | null>(null);
  const [waitlistSubmitError, setWaitlistSubmitError] = useState<string | null>(
    null,
  );
  const [demoErrors, setDemoErrors] = useState<FieldErrors<DemoFormValues>>({});
  const [waitlistErrors, setWaitlistErrors] = useState<
    FieldErrors<WaitlistFormValues>
  >({});
  const [demoForm, setDemoForm] = useState<DemoFormValues>({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "",
    message: "",
  });
  const [waitlistForm, setWaitlistForm] = useState<WaitlistFormValues>({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    companyName: "",
    role: "",
    dealsPerMonth: "",
  });

  const demoValidationSchema = useMemo(
    () =>
      Yup.object({
        firstName: Yup.string()
          .min(2, "First name must be at least 2 characters")
          .required("First name is required"),
        middleName: Yup.string()
          .min(1, "Middle name is required")
          .required("Middle name is required"),
        lastName: Yup.string()
          .min(2, "Last name must be at least 2 characters")
          .required("Last name is required"),
        email: Yup.string()
          .email("Invalid email format")
          .required("Email is required"),
        phone: Yup.string()
          .transform((value) => (value ? value.trim() : value))
          .required("Phone is required")
          .matches(/^[0-9]{10,15}$/, "Phone number must be 10 to 15 digits"),
        role: Yup.string()
          .min(1, "Role is required")
          .required("Role is required"),
        message: Yup.string()
          .min(5, "Message must be at least 5 characters")
          .required("Message is required"),
      }).noUnknown(true),
    [],
  );

  const waitlistValidationSchema = useMemo(
    () =>
      Yup.object({
        firstName: Yup.string()
          .min(2, "First name must be at least 2 characters")
          .required("First name is required"),
        middleName: Yup.string()
          .min(1, "Middle name is required")
          .required("Middle name is required"),
        lastName: Yup.string()
          .min(2, "Last name must be at least 2 characters")
          .required("Last name is required"),
        email: Yup.string()
          .email("Invalid email format")
          .required("Email is required"),
        companyName: Yup.string()
          .min(2, "Company / brokerage name must be at least 2 characters")
          .required("Company / brokerage name is required"),
        role: Yup.string()
          .min(1, "Role is required")
          .required("Role is required"),
        dealsPerMonth: Yup.string()
          .min(1, "Deals per month is required")
          .required("Deals per month is required"),
      }).noUnknown(true),
    [],
  );

  const mapYupErrorsToFields = <T,>(err: any): FieldErrors<T> => {
    const errors: FieldErrors<T> = {};
    const inner = err?.inner;

    if (Array.isArray(inner)) {
      for (const e of inner) {
        const path = e?.path as keyof T | undefined;
        if (!path) continue;
        (errors as Record<string, string | undefined>)[
          path as unknown as string
        ] = e?.message;
      }
    } else if (err?.path) {
      (errors as Record<string, string | undefined>)[err.path as string] =
        err?.message;
    }

    return errors;
  };

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDemoSubmitError(null);
    setDemoSuccess(false);
    try {
      const normalized: DemoFormValues = {
        ...demoForm,
        phone: demoForm.phone.replace(/\D/g, ""),
        middleName: demoForm.middleName?.trim(),
        message: demoForm.message?.trim(),
        firstName: demoForm.firstName?.trim(),
        lastName: demoForm.lastName?.trim(),
        email: demoForm.email?.trim(),
        role: demoForm.role,
      };

      await demoValidationSchema.validate(normalized, { abortEarly: false });
      setDemoErrors({});

      const payload: DemoRequestPayload = {
        firstName : normalized.firstName,
        middleName : normalized.middleName,
        lastName : normalized.lastName,
        email: normalized.email || undefined,
        phone: normalized.phone || undefined,
        companyName: undefined,
        message: normalized.message,
        role: normalized.role
      };

      setDemoLoading(true);
      await createDemoRequest(payload);
      setDemoSuccess(true);

      setDemoForm({
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        phone: "",
        role: "",
        message: "",
      });
    } catch (err: any) {
      const isValidation =
        err?.name === "ValidationError" || Array.isArray(err?.inner);
      if (isValidation) {
        setDemoErrors(mapYupErrorsToFields<DemoFormValues>(err));
      } else {
        setDemoSubmitError(getApiErrorMessage(err));
      }
    } finally {
      setDemoLoading(false);
    }
  };

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWaitlistSubmitError(null);
    setWaitlistSuccess(false);
    try {
      const normalized: WaitlistFormValues = {
        ...waitlistForm,
        middleName: waitlistForm.middleName?.trim(),
        companyName: waitlistForm.companyName?.trim(),
        firstName: waitlistForm.firstName?.trim(),
        lastName: waitlistForm.lastName?.trim(),
        email: waitlistForm.email?.trim(),
        role: waitlistForm.role,
        dealsPerMonth: waitlistForm.dealsPerMonth,
      };

      await waitlistValidationSchema.validate(normalized, {
        abortEarly: false,
      });
      setWaitlistErrors({});

      const payload: WaitlistPayload = {
        role: normalized.role,
        firstName : normalized.firstName,
        middleName : normalized.middleName,
        lastName : normalized.lastName,
        email: normalized.email || undefined,
        companyName: normalized.companyName,
        dealsPerMonth: normalized.dealsPerMonth
      };

      setWaitlistLoading(true);
      await joinWaitlist(payload);
      setWaitlistSuccess(true);

      setWaitlistForm({
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        companyName: "",
        role: "",
        dealsPerMonth: "",
      });
    } catch (err: any) {
      const isValidation =
        err?.name === "ValidationError" || Array.isArray(err?.inner);
      if (isValidation) {
        setWaitlistErrors(mapYupErrorsToFields<WaitlistFormValues>(err));
      } else {
        setWaitlistSubmitError(getApiErrorMessage(err));
      }
    } finally {
      setWaitlistLoading(false);
    }
  };

  return (
    <section className="forms-section bg-blue-50 py-[90px] px-[5%]" id="demo">
      <div className="text-center max-w-[860px] mx-auto">
        <div className="section-label text-[0.78rem] font-bold uppercase tracking-[0.08em] text-blue-500 mb-3">
          Get Started
        </div>
        <h2 className="section-title text-[clamp(1.6rem,3.5vw,2.4rem)] font-black leading-tight mb-4 text-slate-900">
          See it in action or
          <br />
          reserve your spot
        </h2>
        <p className="section-sub text-xl text-slate-600 max-w-[540px] mx-auto leading-relaxed mb-[56px]">
          Request a personalized demo or join the waitlist to be first in line
          when we launch.
        </p>
      </div>

      <div className="forms-grid grid md:grid-cols-2 gap-[28px] max-w-6xl mx-auto">
        {/* Demo Form */}
        <div className="form-card bg-white rounded-[16px] p-[40px] shadow-[0_4px_24px_rgba(15,36,71,0.1)]">
          <h3 className="text-[1.25rem] font-black text-slate-900 mb-1.5">
            Request a Demo
          </h3>
          <p className="text-sm text-slate-600 mb-[28px]">
            See how The Business Lending Hub can transform your brokerage
            workflow. We'll walk you through the platform personally.
          </p>

          <form onSubmit={handleDemoSubmit} className="text-left">
            {demoSubmitError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm font-semibold text-red-800 text-center">
                {demoSubmitError}
              </div>
            )}

            <div className="form-row grid grid-cols-3 gap-3 mb-4">
              <div className="form-group">
                <label
                  htmlFor="demo-first-name"
                  className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5"
                >
                  First Name
                </label>
                <input
                  id="demo-first-name"
                  type="text"
                  value={demoForm.firstName}
                  onChange={(e) =>
                    setDemoForm((p) => ({ ...p, firstName: e.target.value }))
                  }
                  className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                    demoErrors.firstName
                      ? "border-red-500"
                      : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                  }`}
                  placeholder="Jane"
                />
                {demoErrors.firstName && (
                  <div className="mt-1 text-xs text-red-600 font-semibold">
                    {demoErrors.firstName}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label
                  htmlFor="demo-middle-name"
                  className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5"
                >
                  Middle Name
                </label>
                <input
                  id="demo-middle-name"
                  type="text"
                  value={demoForm.middleName}
                  onChange={(e) =>
                    setDemoForm((p) => ({ ...p, middleName: e.target.value }))
                  }
                  className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                    demoErrors.middleName
                      ? "border-red-500"
                      : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                  }`}
                  placeholder="A."
                />
                {demoErrors.middleName && (
                  <div className="mt-1 text-xs text-red-600 font-semibold">
                    {demoErrors.middleName}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label
                  htmlFor="demo-last-name"
                  className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5"
                >
                  Last Name
                </label>
                <input
                  id="demo-last-name"
                  type="text"
                  value={demoForm.lastName}
                  onChange={(e) =>
                    setDemoForm((p) => ({ ...p, lastName: e.target.value }))
                  }
                  className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                    demoErrors.lastName
                      ? "border-red-500"
                      : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                  }`}
                  placeholder="Smith"
                />
                {demoErrors.lastName && (
                  <div className="mt-1 text-xs text-red-600 font-semibold">
                    {demoErrors.lastName}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group mb-4">
              <label className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">
                Business Email
              </label>
              <input
                type="email"
                value={demoForm.email}
                onChange={(e) =>
                  setDemoForm((p) => ({ ...p, email: e.target.value }))
                }
                className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                  demoErrors.email
                    ? "border-red-500"
                    : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                }`}
                placeholder="jane@yourbrokerage.com"
              />
              {demoErrors.email && (
                <div className="mt-1 text-xs text-red-600 font-semibold">
                  {demoErrors.email}
                </div>
              )}
            </div>

            <div className="form-group mb-4">
              <label className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">
                Phone Number
              </label>
              <input
                type="tel"
                value={demoForm.phone}
                onChange={(e) =>
                  setDemoForm((p) => ({ ...p, phone: e.target.value }))
                }
                className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                  demoErrors.phone
                    ? "border-red-500"
                    : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                }`}
                placeholder="(555) 000-0000"
              />
              {demoErrors.phone && (
                <div className="mt-1 text-xs text-red-600 font-semibold">
                  {demoErrors.phone}
                </div>
              )}
            </div>

            <div className="form-group mb-4">
              <label className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">
                I am a...
              </label>
              <select
                value={demoForm.role}
                onChange={(e) =>
                  setDemoForm((p) => ({ ...p, role: e.target.value }))
                }
                className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                  demoErrors.role
                    ? "border-red-500"
                    : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                }`}
              >
                <option value="">Select your role</option>
                <option value={ROLES.CO_OWNER}>Co-owner</option>
                <option value={ROLES.BUSINESS}>Business</option>
                <option value={ROLES.CUSTOMER}>Customer</option>
                <option value={ROLES.BANKER}>Banker</option>
                <option value="Other">Other</option>
              </select>
              {demoErrors.role && (
                <div className="mt-1 text-xs text-red-600 font-semibold">
                  {demoErrors.role}
                </div>
              )}
            </div>

            <div className="form-group mb-4">
              <label className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">
                Anything you'd like us to know?
              </label>
              <textarea
                value={demoForm.message}
                onChange={(e) =>
                  setDemoForm((p) => ({ ...p, message: e.target.value }))
                }
                className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all min-h-[90px] resize-vertical ${
                  demoErrors.message
                    ? "border-red-500"
                    : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                }`}
                placeholder="Tell us about your current process or what you're hoping to solve..."
              />
              {demoErrors.message && (
                <div className="mt-1 text-xs text-red-600 font-semibold">
                  {demoErrors.message}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={demoLoading}
              variant="contained"
              color="primary"
              startIcon={
                demoLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SlideshowIcon />
                )
              }
              fullWidth
              sx={{
                borderRadius: 2,
                py: 1.5,
                fontWeight: 600,
                fontSize: "1rem",
                mt: 1,
              }}
            >
              {demoLoading ? "Submitting..." : "Request My Demo →"}
            </Button>

            {demoSuccess && (
              <div className="success-msg mt-4 bg-green-50 border border-green-200 rounded-lg p-3.5 text-sm font-semibold text-green-800 text-center">
                ✓ Got it! We'll be in touch within one business day.
              </div>
            )}

            <p className="form-note text-center text-[0.78rem] text-slate-500 mt-3">
              No commitment required. We'll reach out within 1 business day.
            </p>
          </form>
        </div>

        {/* Waitlist Form */}
        <div className="form-card bg-white rounded-[16px] p-[40px] shadow-[0_4px_24px_rgba(15,36,71,0.1)]">
          <h3 className="text-[1.25rem] font-black text-slate-900 mb-1.5">
            Join the Waitlist
          </h3>
          <p className="text-sm text-slate-600 mb-[28px]">
            Be among the first to access The Business Lending Hub when we
            launch. Early members get priority onboarding and founding member
            pricing.
          </p>

          <form onSubmit={handleWaitlistSubmit} className="text-left">
            {waitlistSubmitError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm font-semibold text-red-800 text-center">
                {waitlistSubmitError}
              </div>
            )}

            <div className="form-row grid grid-cols-3 gap-3 mb-4">
              <div className="form-group">
                <label
                  htmlFor="waitlist-first-name"
                  className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5"
                >
                  First Name
                </label>
                <input
                  id="waitlist-first-name"
                  type="text"
                  value={waitlistForm.firstName}
                  onChange={(e) =>
                    setWaitlistForm((p) => ({
                      ...p,
                      firstName: e.target.value,
                    }))
                  }
                  className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                    waitlistErrors.firstName
                      ? "border-red-500"
                      : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                  }`}
                  placeholder="Jane"
                />
                {waitlistErrors.firstName && (
                  <div className="mt-1 text-xs text-red-600 font-semibold">
                    {waitlistErrors.firstName}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label
                  htmlFor="waitlist-middle-name"
                  className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5"
                >
                  Middle Name
                </label>
                <input
                  id="waitlist-middle-name"
                  type="text"
                  value={waitlistForm.middleName}
                  onChange={(e) =>
                    setWaitlistForm((p) => ({
                      ...p,
                      middleName: e.target.value,
                    }))
                  }
                  className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                    waitlistErrors.middleName
                      ? "border-red-500"
                      : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                  }`}
                  placeholder="A."
                />
                {waitlistErrors.middleName && (
                  <div className="mt-1 text-xs text-red-600 font-semibold">
                    {waitlistErrors.middleName}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label
                  htmlFor="waitlist-last-name"
                  className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5"
                >
                  Last Name
                </label>
                <input
                  id="waitlist-last-name"
                  type="text"
                  value={waitlistForm.lastName}
                  onChange={(e) =>
                    setWaitlistForm((p) => ({ ...p, lastName: e.target.value }))
                  }
                  className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                    waitlistErrors.lastName
                      ? "border-red-500"
                      : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                  }`}
                  placeholder="Smith"
                />
                {waitlistErrors.lastName && (
                  <div className="mt-1 text-xs text-red-600 font-semibold">
                    {waitlistErrors.lastName}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group mb-4">
              <label className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">
                Business Email
              </label>
              <input
                type="email"
                value={waitlistForm.email}
                onChange={(e) =>
                  setWaitlistForm((p) => ({ ...p, email: e.target.value }))
                }
                className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                  waitlistErrors.email
                    ? "border-red-500"
                    : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                }`}
                placeholder="jane@yourbrokerage.com"
              />
              {waitlistErrors.email && (
                <div className="mt-1 text-xs text-red-600 font-semibold">
                  {waitlistErrors.email}
                </div>
              )}
            </div>

            <div className="form-group mb-4">
              <label className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">
                Company / Brokerage Name
              </label>
              <input
                type="text"
                value={waitlistForm.companyName}
                onChange={(e) =>
                  setWaitlistForm((p) => ({
                    ...p,
                    companyName: e.target.value,
                  }))
                }
                className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                  waitlistErrors.companyName
                    ? "border-red-500"
                    : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                }`}
                placeholder="Smith Capital Advisors"
              />
              {waitlistErrors.companyName && (
                <div className="mt-1 text-xs text-red-600 font-semibold">
                  {waitlistErrors.companyName}
                </div>
              )}
            </div>

            <div className="form-group mb-4">
              <label className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">
                I am a...
              </label>
              <select
                value={waitlistForm.role}
                onChange={(e) =>
                  setWaitlistForm((p) => ({ ...p, role: e.target.value }))
                }
                className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                  waitlistErrors.role
                    ? "border-red-500"
                    : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                }`}
              >
                <option value="">Select your role</option>
                <option value={ROLES.CO_OWNER}>Co-owner</option>
                <option value={ROLES.BUSINESS}>Business</option>
                <option value={ROLES.CUSTOMER}>Customer</option>
                <option value={ROLES.BANKER}>Banker</option>
                <option value="Other">Other</option>
              </select>
              {waitlistErrors.role && (
                <div className="mt-1 text-xs text-red-600 font-semibold">
                  {waitlistErrors.role}
                </div>
              )}
            </div>

            <div className="form-group mb-4">
              <label className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">
                How many deals do you work per month?
              </label>
              <select
                value={waitlistForm.dealsPerMonth}
                onChange={(e) =>
                  setWaitlistForm((p) => ({
                    ...p,
                    dealsPerMonth: e.target.value,
                  }))
                }
                className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                  waitlistErrors.dealsPerMonth
                    ? "border-red-500"
                    : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                }`}
              >
                <option value="">Select a range</option>
                <option value="1-5">1–5 deals</option>
                <option value="6-15">6–15 deals</option>
                <option value="16-30">16–30 deals</option>
                <option value="30+">30+ deals</option>
              </select>
              {waitlistErrors.dealsPerMonth && (
                <div className="mt-1 text-xs text-red-600 font-semibold">
                  {waitlistErrors.dealsPerMonth}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={waitlistLoading}
              variant="contained"
              color="primary"
              startIcon={
                waitlistLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PersonAddAlt1Icon />
                )
              }
              fullWidth
              sx={{
                borderRadius: 2,
                py: 1.5,
                fontWeight: 600,
                fontSize: "1rem",
                mt: 1,
              }}
            >
              {waitlistLoading ? "Submitting..." : "Join the Waitlist →"}
            </Button>

            {waitlistSuccess && (
              <div className="success-msg mt-4 bg-green-50 border border-green-200 rounded-lg p-3.5 text-sm font-semibold text-green-800 text-center">
                ✓ You're on the list! We'll notify you as soon as we launch.
              </div>
            )}

            <p className="form-note text-center text-[0.78rem] text-slate-500 mt-3">
              🔒 No spam. Unsubscribe anytime. Your info stays private.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default FormsSection;
